import {
  useEffect, useRef, useState, useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  setSelectedCalendar,
  fetchPlanner,
  setSelectedCalendarName,
} from '../ducks/plannerActions';

/**
 * Helper to get account-specific localStorage keys
 */
const getStorageKey = (accountCode, key) => `teambuildr_planner_${accountCode}_${key}`;

/**
 * Hook to manage calendar persistence and restoration.
 * Handles localStorage, account switching, and calendar restoration.
 *
 * @param {Object} options
 * @param {Object} options.currentUser - Current user object with accountCode
 * @param {Object} options.loadedWeekRange - { fromWeek, toWeek }
 * @param {Function} options.onInitializeWeekRange - Callback to initialize week range
 * @returns {Object} - { isRestoringCalendar, hasInitialized, handleCalendarSelect,
 *   getStorageKeyForAccount }
 */
const usePlannerCalendarPersistence = ({
  currentUser,
  loadedWeekRange,
  onInitializeWeekRange,
}) => {
  const dispatch = useDispatch();

  // Check localStorage synchronously before first render to avoid flicker
  const getInitialRestoringState = () => {
    if (typeof window === 'undefined') {
      return false;
    }

    // If we do not yet know the account, hold the restoring state to prevent UI flicker
    if (!currentUser?.accountCode) {
      return true;
    }

    try {
      const key = `teambuildr_planner_${currentUser.accountCode}_last_calendar`;
      return localStorage.getItem(key) !== null;
    } catch (e) {
      // localStorage may be disabled or throw in private browsing mode
      return false;
    }
  };

  // Local state for remembering last calendar
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isRestoringCalendar, setIsRestoringCalendar] = useState(
    getInitialRestoringState,
  );

  // Track the account code to detect account switches
  const previousAccountCodeRef = useRef(currentUser?.accountCode);

  // Reset state when account changes (e.g., switch account feature)
  useEffect(() => {
    const currentAccountCode = currentUser?.accountCode;
    const previousAccountCode = previousAccountCodeRef.current;

    if (
      previousAccountCode
      && currentAccountCode
      && previousAccountCode !== currentAccountCode
    ) {
      // Reset initialization state to trigger re-initialization
      setHasInitialized(false);
      // Clear selected calendar since it belongs to the old account
      dispatch(setSelectedCalendar(null));
      dispatch(setSelectedCalendarName(null));
    }

    previousAccountCodeRef.current = currentAccountCode;
  }, [currentUser?.accountCode, dispatch]);

  // Initialize planner with default values
  useEffect(() => {
    if (!hasInitialized && currentUser?.accountCode) {
      // Initialize week range (current week +/- 24 weeks)
      if (!loadedWeekRange.fromWeek || !loadedWeekRange.toWeek) {
        onInitializeWeekRange();
      }

      // Try to restore last selected calendar from localStorage (account-specific)
      let lastCalendarId = null;
      let lastCalendarType = null;
      let lastCalendarName = null;

      try {
        lastCalendarId = localStorage.getItem(
          getStorageKey(currentUser.accountCode, 'last_calendar'),
        );
        lastCalendarType = localStorage.getItem(
          getStorageKey(currentUser.accountCode, 'last_calendar_type'),
        );
        lastCalendarName = localStorage.getItem(
          getStorageKey(currentUser.accountCode, 'last_calendar_name'),
        );
      } catch (e) {
        // localStorage may be disabled or throw in private browsing mode
      }

      if (lastCalendarId) {
        setIsRestoringCalendar(true);
        // Convert to integer and validate
        const idInt = parseInt(lastCalendarId, 10);
        if (Number.isNaN(idInt)) {
          // Invalid stored ID, clear and skip restoration
          setIsRestoringCalendar(false);
          setHasInitialized(true);
          return;
        }

        dispatch(setSelectedCalendar(idInt));
        if (lastCalendarName) {
          dispatch(setSelectedCalendarName(lastCalendarName));
        }

        // Pass calendarId or userId based on stored type
        const fetchPromise = lastCalendarType === 'athlete'
          ? dispatch(fetchPlanner(currentUser.accountCode, null, idInt))
          : dispatch(fetchPlanner(currentUser.accountCode, idInt, null));

        fetchPromise
          .then(() => {
            setIsRestoringCalendar(false);
          })
          .catch(() => {
            // If restore fails, clear the selection and localStorage
            dispatch(setSelectedCalendar(null));
            dispatch(setSelectedCalendarName(null));
            try {
              localStorage.removeItem(
                getStorageKey(currentUser.accountCode, 'last_calendar'),
              );
              localStorage.removeItem(
                getStorageKey(currentUser.accountCode, 'last_calendar_type'),
              );
              localStorage.removeItem(
                getStorageKey(currentUser.accountCode, 'last_calendar_name'),
              );
            } catch (e) {
              // Ignore localStorage errors during cleanup
            }
            setIsRestoringCalendar(false);
          });
      }
      if (!lastCalendarId) {
        setIsRestoringCalendar(false);
      }

      setHasInitialized(true);
    }
  }, [hasInitialized, currentUser, loadedWeekRange, dispatch, onInitializeWeekRange]);

  // Handle calendar selection
  const handleCalendarSelect = useCallback((idValue, type, calendarName) => {
    // Handle clearing selection (null/undefined idValue)
    if (idValue == null) {
      dispatch(setSelectedCalendar(null));
      dispatch(setSelectedCalendarName(null));
      return;
    }

    // Convert to integer and validate
    const idInt = parseInt(idValue, 10);
    if (Number.isNaN(idInt)) {
      // Invalid ID, don't dispatch invalid state
      return;
    }

    dispatch(setSelectedCalendar(idInt));
    dispatch(setSelectedCalendarName(calendarName));

    if (currentUser?.accountCode) {
      // Pass calendarId or userId based on type
      if (type === 'athlete') {
        dispatch(fetchPlanner(currentUser.accountCode, null, idInt));
      } else {
        dispatch(fetchPlanner(currentUser.accountCode, idInt, null));
      }
    }

    // Remember last selected calendar (ID, type, and name) - account-specific
    if (currentUser?.accountCode) {
      try {
        localStorage.setItem(
          getStorageKey(currentUser.accountCode, 'last_calendar'),
          idValue,
        );
        localStorage.setItem(
          getStorageKey(currentUser.accountCode, 'last_calendar_type'),
          type,
        );
        if (calendarName) {
          localStorage.setItem(
            getStorageKey(currentUser.accountCode, 'last_calendar_name'),
            calendarName,
          );
        }
      } catch (e) {
        // localStorage may be full or disabled; continue without persisting
      }
    }
  }, [currentUser?.accountCode, dispatch]);

  // Export getStorageKey for use in parent component (for onCalendarNameFound callback)
  const getStorageKeyForAccount = useCallback((key) => {
    if (!currentUser?.accountCode) return null;
    return getStorageKey(currentUser.accountCode, key);
  }, [currentUser?.accountCode]);

  return {
    isRestoringCalendar,
    hasInitialized,
    handleCalendarSelect,
    getStorageKeyForAccount,
  };
};

export default usePlannerCalendarPersistence;
